import _ from "lodash";
import { type CSSProperties, useState } from "react";
import { type LucideIcon } from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

const SIZES = { xs: 16, sm: 20, md: 26, lg: 32, xl: 40 } as const;
const TEXT_LEVELS = {
  xs: "body-xs",
  sm: "body-sm",
  md: "body-md",
  lg: "body-lg",
  xl: "title-lg",
} as const;
const ICON_SIZES = {
  xs: "xs",
  sm: "xs",
  md: "sm",
  lg: "md",
  xl: "lg",
} as const;

/**
 * A company's logo, beside ``BoxForEntity``'s square for the business:
 * Brandfetch's icon for its domain, loaded straight from Brandfetch's CDN. It
 * shows once it has loaded - a skeleton until then, never the browser's
 * broken image - and stands without a border, in the room its letter's box
 * takes; or, ``isLogoFlush``, to the square's edge, with nothing round it. With
 * no logo - no domain, or one Brandfetch does not know, which answers 404 - its
 * first letter stands in, in a bordered box, so a row keeps its shape; or,
 * given a ``fallbackIcon`` (the icon of the record's kind), that icon in the
 * bordered, filled box.
 */
export function BoxForOrganization({
  organization,
  size = "md",
  fallbackIcon,
  isLogoFlush = false,
  className,
  style,
  "data-testid": dataTestId = "box-for-organization",
}: {
  organization?: { name?: string | null; logoUrl?: string | null } | null;
  /** One of its sizes, or a square of so many pixels: its letter or icon then at their own size. */
  size?: keyof typeof SIZES | number;
  fallbackIcon?: LucideIcon;
  /** The logo is the square, as a reference's own chip draws it: it fills it, with no border's room or ``sx`` padding round it. */
  isLogoFlush?: boolean;
  /** Where the square sits and its shape, where a caller draws it as part of its own. */
  className?: string;
  style?: CSSProperties;
  "data-testid"?: string;
}) {
  // By the logo they were of, so another organization's starts over.
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [unloadableUrl, setUnloadableUrl] = useState<string | null>(null);
  const sizeValue = typeof size === "number" ? size : SIZES[size];
  const logoUrl =
    organization?.logoUrl && organization.logoUrl !== unloadableUrl
      ? organization.logoUrl
      : null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          data-testid={dataTestId}
          className={cn(
            "flex items-center justify-center overflow-hidden rounded select-none",
            "w-(--box-size) h-(--box-size) min-w-(--box-size) min-h-(--box-size)",
            // A logo stands on nothing: no fill behind it to show through its
            // transparent border as a frame.
            logoUrl
              ? "bg-transparent"
              : fallbackIcon
                ? "bg-accent"
                : "bg-muted",
            // Flush, the logo is the square: no border, nor a caller's padding.
            logoUrl && isLogoFlush
              ? "[border-width:0] [border-style:none] border-[currentcolor]"
              : [
                  "[border-width:1px] [border-style:solid]",
                  logoUrl ? "border-transparent" : "border-border",
                ],
            className,
            logoUrl && isLogoFlush && "p-0",
          )}
          style={{ "--box-size": `${sizeValue}px`, ...style } as CSSProperties}
        >
          {logoUrl ? (
            <Skeleton
              loading={loadedUrl !== logoUrl}
              variant="rectangular"
              className="w-full h-full"
            >
              <img
                src={logoUrl}
                alt=""
                onLoad={() => setLoadedUrl(logoUrl)}
                onError={() => setUnloadableUrl(logoUrl)}
                className={cn(
                  "w-full h-full",
                  isLogoFlush ? "object-cover" : "object-contain",
                )}
              />
            </Skeleton>
          ) : fallbackIcon ? (
            <Icon
              ReactIcon={fallbackIcon}
              size={typeof size === "number" ? undefined : ICON_SIZES[size]}
              style={{ color: "var(--muted-foreground)" }}
            />
          ) : (
            <Typography
              level={typeof size === "number" ? undefined : TEXT_LEVELS[size]}
              className="text-muted-foreground/80 pointer-events-none"
            >
              {_.capitalize(organization?.name ?? "")[0] ?? ""}
            </Typography>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom">{organization?.name ?? ""}</TooltipContent>
    </Tooltip>
  );
}
